import React, { useEffect } from "react";
import { Button } from "./Button";

export interface PopupButton {
  /** The text content of the button */
  text: string;
  /** Function to be called when button is clicked */
  onClick: () => void;
  /** Button variant - secondary is default (black button) */
  variant?: "primary" | "secondary" | "tertiary" | "light" | "nav";
  /** Whether the button is in loading state */
  isLoading?: boolean;
  /** Text to show when button is in loading state */
  loadingText?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
}

interface PopupProps {
  /** Whether the popup is visible */
  isOpen: boolean;
  /** Function to be called when popup should be closed (e.g., clicking backdrop) */
  onClose?: () => void;
  /** Title text displayed at the top of the popup */
  title: string;
  /** The main content of the popup */
  children: React.ReactNode;
  /** Array of buttons to display at the bottom */
  buttons?: PopupButton[];
  /** Maximum width of the popup (default: 2xl) */
  maxWidth?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
  /** Maximum height of the popup (default: 80vh) */
  maxHeight?: string;
  /** Whether clicking the backdrop should close the popup (default: true) */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape should close the popup (default: true) */
  closeOnEscape?: boolean;
  /** Additional CSS classes for the popup container */
  className?: string;
  /** Additional CSS classes for the content area */
  contentClassName?: string;
  /** Whether the content area should be scrollable (default: true) */
  scrollable?: boolean;
  /** Whether the popup should size to its content instead of filling the viewport height (default: false) */
  autoHeight?: boolean;
}

export const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  title,
  children,
  buttons = [],
  maxWidth = "2xl",
  maxHeight = "80vh",
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = "",
  contentClassName = "",
  scrollable = true,
  autoHeight = false,
}) => {
  // Handle escape key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape || !onClose) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      closeOnBackdropClick &&
      onClose &&
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop with blur/grey effect */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Popup container */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-4">
        <div
          className={`
            container-white-rounded
            border-container
            border-black
            w-full
            pointer-events-auto
            flex
            flex-col
            ${autoHeight ? "h-auto" : "h-[85vh]"}
            max-w-[670px]
            ${className}
          `}
        >
          {/* Header */}
          <div className="flex-none pt-[10px]">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-2xl font-bold text-txt-base">{title}</h2>
              {onClose && (
                <button
                  onClick={onClose}
                  className="
                    p-2 
                    rounded-full 
                    hover:bg-neutral-100 
                    transition-colors 
                    duration-200
                    focus:outline-none
                    focus:ring-2
                    focus:ring-neutral-300
                  "
                  aria-label="Close popup"
                >
                  <svg
                    className="w-6 h-6 text-neutral-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className={autoHeight ? "relative" : "flex-1 relative min-h-0"}>
            <div
              className={`
                ${autoHeight ? "max-h-[60vh]" : "absolute inset-0"}
                pb-6
                px-1
                ${scrollable ? "overflow-y-auto" : "overflow-hidden"}
                ${contentClassName}
              `}
            >
              {children}
            </div>
            {/* Scroll indicator blur - only show when there are buttons and content is scrollable */}
            {buttons.length > 0 && scrollable && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            )}
          </div>

          {/* Footer with buttons */}
          {buttons.length > 0 && (
            <div className="flex-none py-[10px]  bg-white rounded-b-2xl">
              <div className="flex justify-center space-x-4 flex-wrap gap-2">
                {buttons.map((button, index) => (
                  <Button
                    key={index}
                    text={button.text}
                    onClick={button.onClick}
                    variant={button.variant}
                    isLoading={button.isLoading}
                    loadingText={button.loadingText}
                    disabled={button.disabled}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
